import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle, Edit, RefreshCw, Save, Trash2, X } from 'lucide-react';
import MainService from '../../services/MainService';

const PAGE_SIZE = 20;
const todayInput = () => new Date().toISOString().split('T')[0];

const emptyForm = () => ({
  Id: 0,
  Amount: 0,
  Remarks: '',
  Date: todayInput(),
  ExpenseId: '',
  IsLinked: false,
});

const parseList = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const parseTotal = (response, fallback = 0) => Number(response?.Total ?? response?.total ?? fallback) || 0;

const isSuccess = (response) => response === true || response === 'true' || response === 'Success' || response?.Success === true || response?.success === true;

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('en-GB');
};

const formatAmount = (value) => Number(value || 0).toFixed(2);

const dateParts = (value) => {
  const date = value ? new Date(value) : new Date();
  return {
    Day: date.getDate(),
    Month: date.getMonth() + 1,
    Year: date.getFullYear(),
  };
};

const CompanyExpenses = () => {
  const [form, setForm] = useState(emptyForm());
  const [expenses, setExpenses] = useState([]);
  const [rows, setRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const loadLookups = useCallback(async () => {
    try {
      setExpenses(parseList(await MainService.ExpensesInDropDown()));
    } catch (err) {
      console.error('Load company expense lookups error:', err);
      setError('Failed to load expenses');
    }
  }, []);

  const loadRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.CompanyExpenseInGrid({
        Skip: (currentPage - 1) * PAGE_SIZE,
        Take: PAGE_SIZE,
      });
      const list = parseList(response);
      setRows(list);
      setTotalCount(parseTotal(response, list.length));
    } catch (err) {
      console.error('Load company expenses error:', err);
      setRows([]);
      setTotalCount(0);
      setError('Failed to load company expenses');
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    loadLookups();
  }, [loadLookups]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const updateForm = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const refresh = () => {
    setForm(emptyForm());
    setCurrentPage(1);
    loadRows();
  };

  const saveData = async () => {
    if (Number(form.Amount) <= 0) {
      setError('Enter Amount');
      return;
    }
    if (!form.ExpenseId) {
      setError('Select Expense');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = {
        ...form,
        Amount: Number(form.Amount) || 0,
        ExpenseId: Number(form.ExpenseId) || 0,
        ...dateParts(form.Date),
      };
      const response = await MainService.SaveCompanyExpense(payload);
      if (isSuccess(response)) {
        setSuccess('Success');
        setForm(emptyForm());
        await loadRows();
      } else {
        setError(String(response || 'Error'));
      }
    } catch (err) {
      console.error('Save company expense error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const editRow = (row) => {
    setForm({
      ...row,
      Date: row.Date ? new Date(row.Date).toISOString().split('T')[0] : todayInput(),
      ExpenseId: row.ExpenseId || row.Expense?.Id || '',
      Amount: row.Amount || 0,
      Remarks: row.Remarks || '',
      IsLinked: Boolean(row.IsLinked),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const confirmDelete = async () => {
    if (!deleteTarget?.Id) return;

    try {
      setSaving(true);
      setError('');
      const response = await MainService.DeleteCompanyExpense(deleteTarget.Id);
      if (isSuccess(response)) {
        setSuccess('Deleted');
        setDeleteTarget(null);
        await loadRows();
      } else {
        setError(String(response || 'Failed to delete'));
      }
    } catch (err) {
      console.error('Delete company expense error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 border-b border-gray-200 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">Company Expenses</h1>
          <p className="mt-1 text-sm text-gray-500">Add, edit, and delete company expense entries.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <FilterSelect label="Expense" value={form.ExpenseId} onChange={(value) => updateForm('ExpenseId', value)} options={expenses} />
          <FilterInput label="Date" type="date" value={form.Date} onChange={(value) => updateForm('Date', value)} />
          <FilterInput label="Amount" type="number" value={form.Amount} onChange={(value) => updateForm('Amount', value)} />
          <FilterInput label="Remarks" value={form.Remarks} onChange={(value) => updateForm('Remarks', value)} />
        </div>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <button type="button" onClick={refresh} disabled={loading || saving} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button type="button" onClick={saveData} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50">
            <Save className="h-4 w-4" />
            Save
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 p-4">
          <h2 className="text-lg font-semibold text-gray-900">Company Expense List</h2>
          <p className="text-sm text-gray-500">Showing {rows.length} of {totalCount} expenses</p>
        </div>
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[950px] divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Date</Th>
                <Th>Expense</Th>
                <Th>Amount</Th>
                <Th>Remarks</Th>
                <Th>Created By</Th>
                <Th align="right">Options</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading ? (
                <EmptyRow colSpan={6} text="Loading company expenses..." />
              ) : rows.length === 0 ? (
                <EmptyRow colSpan={6} text="No company expenses found." />
              ) : (
                rows.map((row) => (
                  <tr key={row.Id} className="hover:bg-gray-50">
                    <Td>{formatDate(row.Date)}</Td>
                    <Td strong>{row.Expense?.Name || '-'}</Td>
                    <Td>{formatAmount(row.Amount)}</Td>
                    <Td>{row.Remarks || '-'}</Td>
                    <Td>{row.CreatedBy?.UserName || '-'}</Td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => editRow(row)} disabled={saving} className="rounded-lg bg-blue-50 p-2 text-blue-700 hover:bg-blue-100 disabled:opacity-50" title="Edit">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => setDeleteTarget(row)} disabled={saving} className="rounded-lg bg-red-50 p-2 text-red-700 hover:bg-red-100 disabled:opacity-50" title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TableFooter currentPage={currentPage} totalPages={totalPages} totalCount={totalCount} rows={rows} loading={loading} onPageChange={setCurrentPage} />
      </div>

      {deleteTarget && (
        <ConfirmModal
          title="Confirm Delete"
          message="Click confirm to delete this company expense."
          saving={saving}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={confirmDelete}
        />
      )}
    </div>
  );
};

const FilterInput = ({ label, value, onChange, type = 'text' }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    <input type={type} value={value} onChange={(event) => onChange(event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" />
  </div>
);

const FilterSelect = ({ label, value, onChange, options }) => (
  <div>
    <label className="mb-1 block text-sm font-medium text-gray-700">{label}</label>
    <select value={value} onChange={(event) => onChange(event.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20">
      <option value="">Select {label}</option>
      {options.map((option) => <option key={option.Id} value={option.Id}>{option.Name}</option>)}
    </select>
  </div>
);

const TableFooter = ({ currentPage, totalPages, totalCount, rows, loading, onPageChange }) => (
  <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
    <span>Showing {rows.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0} to {Math.min(currentPage * PAGE_SIZE, totalCount)} of {totalCount}</span>
    <div className="flex gap-2">
      <button type="button" onClick={() => onPageChange((page) => Math.max(1, page - 1))} disabled={currentPage <= 1 || loading} className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Previous</button>
      <span className="px-3 py-1.5 font-semibold text-gray-700">Page {currentPage} of {totalPages}</span>
      <button type="button" onClick={() => onPageChange((page) => Math.min(totalPages, page + 1))} disabled={currentPage >= totalPages || loading} className="rounded-lg border border-gray-300 px-3 py-1.5 font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
    </div>
  </div>
);

const ConfirmModal = ({ title, message, saving, onCancel, onConfirm }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
      <div className="border-b border-gray-200 p-5">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
      </div>
      <div className="p-5 text-sm text-gray-600">{message}</div>
      <div className="flex justify-end gap-3 border-t border-gray-200 p-5">
        <button type="button" onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50">Cancel</button>
        <button type="button" onClick={onConfirm} disabled={saving} className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-50">Confirm</button>
      </div>
    </div>
  </div>
);

const Th = ({ children, align = 'left' }) => <th className={`whitespace-nowrap px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500 ${align === 'right' ? 'text-right' : 'text-left'}`}>{children}</th>;
const Td = ({ children, strong = false }) => <td className={`whitespace-nowrap px-4 py-3 text-sm ${strong ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{children}</td>;
const EmptyRow = ({ colSpan, text }) => <tr><td colSpan={colSpan} className="px-4 py-10 text-center text-sm text-gray-500">{text}</td></tr>;

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[280px] items-center gap-3 rounded-lg px-4 py-3 text-white shadow-lg ${isError ? 'bg-red-500' : 'bg-green-500'}`}>
      {isError ? <AlertTriangle className="h-5 w-5" /> : <CheckCircle className="h-5 w-5" />}
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button type="button" onClick={onClose} className="rounded p-1 hover:bg-white/20">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default CompanyExpenses;
